

























































































































































import AnalyticsDateRangeSelectorDialog from "@/components/analytics/AnalyticsDateRangeSelectorDialog.vue";
import BarChartCard from "@/components/analytics/BarChartCard.vue";
import PieChartCard from "@/components/analytics/PieChartCard.vue";
import TimeSeriesLineChartCard from "@/components/analytics/TimeSeriesLineChartCard.vue";
import MyDamageDetailSideCard from "@/components/report/user/MyDamageDetailSideCard.vue";
import Notifications from "@/components/thg/Notifications.vue";
import TableWrapper from "@/components/utility/TableWrapper.vue";
import LayoutReportWithBanner from "@/layouts/LayoutReportWithBanner.vue";
import { IBreadcrumb } from "@/lib/interfaces/utility/breadcrumb-interface";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { handleError } from "@/lib/utility/handleError";
import { DocumentDirectionEnum } from "@/models/invoice.entity";
import { MrfiktivBaseChartItemViewmodelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { AnalyticsFleetModule } from "@/store/modules/fleet/analytics-fleet.store";
import { FleetManagerModule } from "@/store/modules/fleet/manager-fleet.store";
import { UserModule } from "@/store/modules/me-user.store";
import { ReportModule } from "@/store/modules/report.store";
import { Component, Vue } from "vue-property-decorator";

type DataHandlerItem = {
  isLoading: boolean;
  getData: (fleetId: string) => Promise<void>;
};

type DataHandler = Record<string, DataHandlerItem>;

@Component({
  components: {
    LayoutReportWithBanner,
    Notifications,
    TableWrapper,
    MyDamageDetailSideCard,
    PieChartCard,
    BarChartCard,
    AnalyticsDateRangeSelectorDialog,
    TimeSeriesLineChartCard
  },
  filters: {
    simpleDoubleDigitDate
  }
})
export default class FleetAnalyticsView extends Vue {
  DocumentDirectionEnum = DocumentDirectionEnum;

  isLoading = false;
  selectedId: string | null = null;
  tab = 0;

  dataHandler: DataHandler = {
    coverageTypeCount: {
      getData: async () => {
        await AnalyticsFleetModule.getCoverageTypeData({ partnerId: this.partnerId, fleetId: this.fleetId ?? "" });
      },
      isLoading: false
    },
    damageTypeCount: {
      getData: async () => {
        await AnalyticsFleetModule.getDamageTypeData({ partnerId: this.partnerId, fleetId: this.fleetId ?? "" });
      },
      isLoading: false
    },
    tripTypeCount: {
      getData: async () => {
        await AnalyticsFleetModule.getTripTypeData({ partnerId: this.partnerId, fleetId: this.fleetId ?? "" });
      },
      isLoading: false
    },
    statusValueDistribution: {
      getData: async () => {
        await AnalyticsFleetModule.getStatusValueDistribution({
          partnerId: this.partnerId,
          fleetId: this.fleetId ?? ""
        });
      },
      isLoading: false
    },
    progressStatusDistribution: {
      getData: async () => {
        await AnalyticsFleetModule.getProgressStatusDistribution({
          partnerId: this.partnerId,
          fleetId: this.fleetId ?? ""
        });
      },
      isLoading: false
    },
    weekdayDistribution: {
      getData: async () => {
        await AnalyticsFleetModule.getWeekdayAnalysis({ partnerId: this.partnerId, fleetId: this.fleetId ?? "" });
      },
      isLoading: false
    },
    damageTypeAverage: {
      getData: async () => {
        await AnalyticsFleetModule.getDamageTypeAverageData({ partnerId: this.partnerId, fleetId: this.fleetId ?? "" });
      },
      isLoading: false
    },
    coverageTypeAverage: {
      getData: async () => {
        await AnalyticsFleetModule.getCoverageTypeAverageData({
          partnerId: this.partnerId,
          fleetId: this.fleetId ?? ""
        });
      },
      isLoading: false
    },
    coverageTypeSubtotal: {
      getData: async () => {
        await AnalyticsFleetModule.getCoverageTypeSubtotalData({
          partnerId: this.partnerId,
          fleetId: this.fleetId ?? ""
        });
      },
      isLoading: false
    },
    damageTypeSubtotal: {
      getData: async () => {
        await AnalyticsFleetModule.getDamageTypeSubtotalData({
          partnerId: this.partnerId,
          fleetId: this.fleetId ?? ""
        });
      },
      isLoading: false
    },
    topCreditor: {
      getData: async () => {
        await AnalyticsFleetModule.getTopCreditorData({ partnerId: this.partnerId, fleetId: this.fleetId ?? "" });
      },
      isLoading: false
    },
    reportTrendAnalysis: {
      getData: async () => {
        await AnalyticsFleetModule.getTrendAnalysisDataPerPartnerFleetId({
          partnerId: this.partnerId,
          fleetId: this.fleetId ?? ""
        });
      },
      isLoading: false
    },
    topVehicle: {
      getData: async () => {
        await AnalyticsFleetModule.getTopVehicleData({ partnerId: this.partnerId, fleetId: this.fleetId ?? "" });
      },
      isLoading: false
    }
  };

  get breadCrumbs(): IBreadcrumb[] {
    const breadCrumbs: IBreadcrumb[] = [];

    breadCrumbs.push({
      text: this.$t("nav.FleetTableView.description"),
      disabled: false,
      exact: true,
      to: {
        name: "FleetHomeView",
        params: {}
      }
    });

    breadCrumbs.push({
      text: this.$t("common.nouns.analytics"),
      disabled: false,
      exact: false,
      to: {
        name: "FleetAnalyticsView",
        params: {}
      }
    });

    return breadCrumbs;
  }

  get dates() {
    return AnalyticsFleetModule.dateRange;
  }

  get partnerId() {
    return ReportModule.partner.id;
  }

  get fleetIds() {
    return UserModule.fleetPermission?.filter(p => p.partnerId === this.partnerId).map(p => p.fleetId);
  }

  get fleetId() {
    return FleetManagerModule.current?.id;
  }

  get damageTypeSubtotalData(): MrfiktivBaseChartItemViewmodelGen[] {
    return AnalyticsFleetModule.damageTypeSubtotalOutboundData.map(c => ({
      name: this.$t(`enums.DamageTypeEnum.${c.name}`).toString(),
      value: c.value ?? undefined
    }));
  }

  get coverageTypeSubtotalData(): MrfiktivBaseChartItemViewmodelGen[] {
    return AnalyticsFleetModule.coverageTypeSubtotalOutboundData.map(c => ({
      name: this.$t(`enums.CoverageTypeEnum.${c.name}`).toString(),
      value: c.value ?? undefined
    }));
  }

  get damageTypeCountData(): MrfiktivBaseChartItemViewmodelGen[] {
    return AnalyticsFleetModule.damageTypeData.map(c => ({
      name: this.$t(`enums.DamageTypeEnum.${c.name}`).toString(),
      value: c.value ?? undefined
    }));
  }

  get coverageTypeCountData(): MrfiktivBaseChartItemViewmodelGen[] {
    return AnalyticsFleetModule.coverageTypeData.map(c => ({
      name: this.$t(`enums.CoverageTypeEnum.${c.name}`).toString(),
      value: c.value ?? undefined
    }));
  }

  get tripTypeCountData(): MrfiktivBaseChartItemViewmodelGen[] {
    return AnalyticsFleetModule.tripTypeData.map(c => ({
      name: this.$t(`enums.TripTypeEnum.${c.name}`).toString(),
      value: c.value ?? undefined
    }));
  }

  get privateTripKpiGroup() {
    const total = AnalyticsFleetModule.tripTypeData.reduce((sum, item) => sum + (item.value || 0), 0);
    const privateTrip = AnalyticsFleetModule.tripTypeData.find(c => c.name === "PRIVATE")?.value || 0;
    const percentage = total > 0 ? (privateTrip / total) * 100 : 0;
    return [
      {
        icon: "mdi-percent",
        value: Math.round(percentage * 10) / 10,
        name: this.$t("views.report.fleet.FleetAnalyticsView.privateTripCount")
      }
    ];
  }

  get damageTypeAverageKpiGroup() {
    const values = AnalyticsFleetModule.damageTypeAverageData
      .map(c => c.value)
      .filter((v): v is number => typeof v === "number" && !isNaN(v));
    const sum = values.reduce((accumulator, value) => accumulator + value, 0);
    const average = values.length > 0 ? sum / values.length : 0;
    return [
      {
        icon: "mdi-currency-eur",
        value: Math.round(average),
        name: this.$t("views.report.fleet.FleetAnalyticsView.damageTypeAverageCount")
      }
    ];
  }

  get damageTypeAverageData() {
    return AnalyticsFleetModule.damageTypeAverageData.map(c => ({
      name: this.$t(`enums.DamageTypeEnum.${c.name}`).toString(),
      value: c.value
    }));
  }

  get coverageTypeAverageKpiGroup() {
    const values = AnalyticsFleetModule.coverageTypeAverageData
      .map(c => c.value)
      .filter((v): v is number => typeof v === "number" && !isNaN(v));
    const sum = values.reduce((accumulator, value) => accumulator + value, 0);
    const average = values.length > 0 ? sum / values.length : 0;
    return [
      {
        icon: "mdi-currency-eur",
        value: Math.round(average),
        name: this.$t("views.report.fleet.FleetAnalyticsView.coverageTypeAverageCount")
      }
    ];
  }

  get coverageTypeAverageData() {
    return AnalyticsFleetModule.coverageTypeAverageData.map(c => ({
      name: this.$t(`enums.CoverageTypeEnum.${c.name}`).toString(),
      value: c.value
    }));
  }

  get statusDistributionData() {
    if (this.hasStatusValueDistributionData) {
      return AnalyticsFleetModule.statusValueDistributionData.map(c => ({
        name: c.name,
        value: c.value
      }));
    }
    return AnalyticsFleetModule.progressStatusDistributionData.map(c => ({
      name: this.$t(`partner.common.reportProgressStatus.${c.name}`).toString(),
      value: c.value
    }));
  }

  get statusDistributionKpiGroup() {
    return [
      {
        icon: "mdi-circle-outline",
        value:
          AnalyticsFleetModule.progressStatusDistributionData.find(c => c.name === "new" || c.name === "inProgress")
            ?.value || 0,
        name: this.$t("enums.InvoiceStatusEnum.open")
      },
      {
        icon: "mdi-check-circle-outline",
        value: AnalyticsFleetModule.progressStatusDistributionData.find(c => c.name === "finished")?.value || 0,
        name: this.$t("enums.AnonymizationStatusEnum.Completed")
      }
    ];
  }

  get weekdayDistributionData() {
    return AnalyticsFleetModule.weekdayDistributionData;
  }

  get topCreditorData() {
    return AnalyticsFleetModule.topCreditorData;
  }

  get topVehicleData() {
    return AnalyticsFleetModule.topVehicleData;
  }

  get reportTrendData() {
    const trendData = [
      {
        name: this.$t("views.report.fleet.FleetAnalyticsView.oneDayTrend"),
        type: "line",
        symbol: "none",
        data: AnalyticsFleetModule.trendAnalysisData.data?.dayTrend
      },
      {
        name: this.$t("views.report.fleet.FleetAnalyticsView.sevenDayTrend"),
        type: "line",
        symbol: "none",
        data: AnalyticsFleetModule.trendAnalysisData.data?.sevenDayTend
      },
      {
        name: this.$t("views.report.fleet.FleetAnalyticsView.thirtyDayTrend"),
        type: "line",
        symbol: "none",
        data: AnalyticsFleetModule.trendAnalysisData.data?.thirtyDayTrend
      }
    ];

    return trendData;
  }

  get hasStatusValueDistributionData(): boolean {
    return (
      AnalyticsFleetModule.statusValueDistributionData && AnalyticsFleetModule.statusValueDistributionData.length > 0
    );
  }
  get i18n() {
    return this.$t("views.report.fleet.FleetVehicleView");
  }

  async mounted() {
    if (!this.fleetId) {
      return;
    }

    this.isLoading = true;
    try {
      await this.getData(this.fleetId);
    } catch (error) {
      handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async getData(fleetId: string) {
    Object.keys(this.dataHandler).forEach(async key => {
      this.dataHandler[key].isLoading = true;

      try {
        await this.dataHandler[key].getData(fleetId);
      } catch (error) {
        this.$log.error(error);
      } finally {
        this.dataHandler[key].isLoading = false;
      }
    });
  }

  async setDateRange(dateRange: string[]) {
    AnalyticsFleetModule.setDateRange(dateRange);
    if (this.fleetId) {
      await this.getData(this.fleetId);
    }
  }
}
